import { GamePhase } from "@/hooks/useGameState";

// === Multiplayer (Phase 1: async "vs" over Supabase Realtime) ===
//
// Two players each run their own normal game in parallel and publish a small
// live snapshot of their progress to a shared Realtime channel. There is no
// server-authoritative state and no DB table: the match lives entirely in an
// ephemeral Broadcast + Presence channel keyed by the match id, which keeps it
// dead simple and lets guests play without auth. The trade-off (a refresh drops
// you from the channel until you reopen the link, and scores are client-
// reported) is acceptable for a casual "play against a friend" mode.

export type MatchRole = "host" | "guest";

// The live snapshot each player broadcasts to the other. Deliberately tiny —
// just what the opponent panel needs to render.
export interface MatchPlayerState {
  name: string;
  role: MatchRole;
  score: number;
  rank: string;
  alignmentLabel: string | null;
  wheelNumber: number;
  totalWheels: number;
  phase: GamePhase;
  finished: boolean;
  // The most recent landed result, so the opponent can "spectate" the feed
  // even without seeing the wheel animation itself.
  lastResult: { wheelName: string; name: string; value: number } | null;
  updatedAt: number;
}

// A single spin broadcast the instant a player's wheel starts spinning, so a
// spectating opponent can replay the same animation landing on the same result.
// Sent on its own "spin" channel event (not in MatchPlayerState) to keep the
// frequent state broadcasts lean.
export interface MatchSpinEvent {
  seq: number; // monotonic per player; a new seq = a new spin to animate
  wheelName: string;
  options: { name: string; value: number }[];
  resultIndex: number;
  showValue: boolean;
}

// Short, URL-friendly match id.
export function newMatchId(): string {
  const uuid =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
  return uuid.replace(/-/g, "").slice(0, 10);
}

const GUEST_KEY = "heroOrAVillain:guestIdentity";

export interface GuestIdentity {
  id: string;
  name: string;
}

// A stable per-browser guest identity so an un-signed-in player keeps the same
// name/presence key across a match (and a refresh). Persisted in localStorage.
export function getGuestIdentity(): GuestIdentity {
  if (typeof window === "undefined") {
    return { id: "guest", name: "Guest" };
  }
  const raw = window.localStorage.getItem(GUEST_KEY);
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as GuestIdentity;
      if (parsed?.id && parsed?.name) return parsed;
    } catch {
      /* fall through and regenerate */
    }
  }
  const suffix = Math.floor(100000 + Math.random() * 900000); // 6 digits
  const identity: GuestIdentity = {
    id: `guest-${newMatchId()}`,
    name: `Guest#${suffix}`,
  };
  window.localStorage.setItem(GUEST_KEY, JSON.stringify(identity));
  return identity;
}

// Build the link a host shares with their friend. The friend joins as the guest
// (no role flag); the host keeps `r=h` in their own URL so a refresh restores
// their role instead of colliding as a second guest.
export function buildShareLink(matchId: string): string {
  if (typeof window === "undefined") return "";
  return `${window.location.origin}/?match=${matchId}`;
}
